import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Button,
    Card,
    DatePicker,
    Dropdown,
    Grid,
    Select,
    Space,
    Tabs,
    Typography,
    message,
} from 'antd';
import {
    DownloadOutlined,
    PlusOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { bdoApi } from '../../api/client';
import BdoSummaryCards from './components/BdoSummaryCards';
import BdoTable51 from './components/BdoTable51';
import BdoSalesTab from './components/BdoSalesTab';
import CollectivePackagingTab from './components/CollectivePackagingTab';
import CollectivePackagingModal from './components/CollectivePackagingModal';

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

type TabKey = 'summary' | 'sales' | 'collective';

export default function BdoPage() {
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const queryClient = useQueryClient();

    const currentYear = dayjs().year();
    const [year, setYear] = useState(currentYear);
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
    const [activeTab, setActiveTab] = useState<TabKey>('summary');
    const [collectiveModalOpen, setCollectiveModalOpen] = useState(false);
    const [exporting, setExporting] = useState<string | null>(null);

    const yearOptions = useMemo(() => {
        const years: number[] = [];
        for (let y = currentYear + 1; y >= 2021; y -= 1) years.push(y);
        return years.map((y) => ({ value: y, label: String(y) }));
    }, [currentYear]);

    const range = useMemo(
        () => ({
            year,
            from: dateRange?.[0]?.format('YYYY-MM-DD') || `${year}-01-01`,
            to: dateRange?.[1]?.format('YYYY-MM-DD') || `${year}-12-31`,
        }),
        [year, dateRange],
    );

    const { data: summary, isLoading: summaryLoading, refetch, isFetching } = useQuery({
        queryKey: ['bdo-summary', range.year, range.from, range.to],
        queryFn: () =>
            bdoApi
                .getSummary({ year: range.year, from: range.from, to: range.to })
                .then((res) => res.data),
    });

    const refreshAll = () => {
        queryClient.invalidateQueries({ queryKey: ['bdo-summary'] });
        queryClient.invalidateQueries({ queryKey: ['bdo-sales'] });
        queryClient.invalidateQueries({ queryKey: ['bdo-collective'] });
        refetch();
    };

    const handleYearChange = (value: number) => {
        setYear(value);
        setDateRange(null);
    };

    const handleExport = async (
        format: 'xlsx' | 'csv',
        section?: 'summary' | 'sales' | 'collective',
    ) => {
        const key = `${format}-${section || 'all'}`;
        setExporting(key);
        try {
            const response = await bdoApi.exportData({
                format,
                section: section || 'summary',
                year: range.year,
                from: range.from,
                to: range.to,
            });

            const disposition: string =
                response.headers?.['content-disposition'] || '';
            const match = disposition.match(/filename="?([^";]+)"?/i);
            const fallback =
                format === 'xlsx'
                    ? `BDO_${range.year}.xlsx`
                    : `BDO_${range.year}_${section || 'summary'}.csv`;
            const filename = match
                ? decodeURIComponent(match[1].replace(/"/g, ''))
                : fallback;

            const blob = new Blob([response.data], {
                type:
                    response.headers?.['content-type'] ||
                    (format === 'xlsx'
                        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                        : 'text/csv;charset=utf-8'),
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            message.success('Eksport wygenerowany');
        } catch {
            message.error('Nie udało się wygenerować eksportu');
        } finally {
            setExporting(null);
        }
    };

    const filters = (
        <Space wrap={isMobile} style={{ width: isMobile ? '100%' : 'auto' }}>
            <Select
                value={year}
                options={yearOptions}
                onChange={handleYearChange}
                style={{ width: isMobile ? '100%' : 110 }}
                aria-label="Rok"
            />
            <RangePicker
                value={dateRange}
                onChange={(values) =>
                    setDateRange(
                        values && values[0] && values[1]
                            ? [values[0], values[1]]
                            : null,
                    )
                }
                format="DD.MM.YYYY"
                style={{ width: isMobile ? '100%' : 260 }}
                disabledDate={(current) =>
                    !current || current.year() !== year
                }
                allowClear
                placeholder={['Od (opcjonalnie)', 'Do (opcjonalnie)']}
            />
        </Space>
    );

    const actions = (
        <Space wrap={isMobile} style={{ width: isMobile ? '100%' : 'auto' }}>
            <Button
                icon={<ReloadOutlined />}
                onClick={refreshAll}
                loading={isFetching}
                style={{ flex: isMobile ? 1 : 'none' }}
            >
                Odśwież
            </Button>
            <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCollectiveModalOpen(true)}
                style={{ flex: isMobile ? 1 : 'none' }}
            >
                Dodaj opakowanie zbiorcze
            </Button>
            <Button
                icon={<DownloadOutlined />}
                loading={exporting === 'xlsx-all'}
                onClick={() => handleExport('xlsx')}
                style={{ flex: isMobile ? 1 : 'none' }}
            >
                Eksport Excel
            </Button>
            <Dropdown
                menu={{
                    items: [
                        { key: 'summary', label: 'Podsumowanie (CSV)' },
                        { key: 'sales', label: 'Sprzedaż (CSV)' },
                        { key: 'collective', label: 'Opakowania zbiorcze (CSV)' },
                    ],
                    onClick: ({ key }) =>
                        handleExport('csv', key as TabKey),
                }}
            >
                <Button
                    icon={<DownloadOutlined />}
                    loading={exporting?.startsWith('csv')}
                    style={{ flex: isMobile ? 1 : 'none' }}
                >
                    Eksport CSV {!isMobile && '▾'}
                </Button>
            </Dropdown>
        </Space>
    );

    const tabItems = [
        {
            key: 'summary',
            label: 'Podsumowanie',
            children: (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <BdoSummaryCards
                        summary={summary}
                        loading={summaryLoading}
                        isMobile={isMobile}
                    />
                    <BdoTable51
                        summary={summary}
                        loading={summaryLoading}
                        isMobile={isMobile}
                    />
                    {summary?.generatedAt && (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            Wygenerowano:{' '}
                            {dayjs(summary.generatedAt).format('DD.MM.YYYY HH:mm')} · Zakres{' '}
                            {dayjs(range.from).format('DD.MM.YYYY')} –{' '}
                            {dayjs(range.to).format('DD.MM.YYYY')} · Zamówienia:{' '}
                            {summary.stats?.ordersCount ?? 0} · Pozycje:{' '}
                            {summary.stats?.linesCount ?? 0}
                        </Text>
                    )}
                </Space>
            ),
        },
        {
            key: 'sales',
            label: 'Sprzedaż',
            children: <BdoSalesTab range={range} isMobile={isMobile} />,
        },
        {
            key: 'collective',
            label: 'Opakowania zbiorcze',
            children: <CollectivePackagingTab range={range} isMobile={isMobile} />,
        },
    ];

    return (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Card size="small">
                <Space
                    direction={isMobile ? 'vertical' : 'horizontal'}
                    style={{
                        width: '100%',
                        justifyContent: 'space-between',
                        alignItems: isMobile ? 'stretch' : 'center',
                    }}
                    size="middle"
                >
                    <Space direction="vertical" size={0}>
                        <Title level={4} style={{ margin: 0 }}>
                            BDO — raport opakowań
                        </Title>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Tabela 5.1 dla opakowań ≤1 Mg · Wielomateriałowe sumowane
                            osobno (5.2)
                        </Text>
                    </Space>
                    {filters}
                    {actions}
                </Space>
            </Card>

            <Tabs
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key as TabKey)}
                items={tabItems}
                size={isMobile ? 'small' : 'middle'}
            />

            <CollectivePackagingModal
                open={collectiveModalOpen}
                onClose={() => setCollectiveModalOpen(false)}
                entry={null}
                defaultDate={(() => {
                    const today = dayjs().format('YYYY-MM-DD');
                    return today >= range.from && today <= range.to ? today : range.to;
                })()}
            />
        </Space>
    );
}
